import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";

const FALLBACK_TEMPLATES = [
  { key: "COMPOSE", label: "Blank compose", subject: "", body: "" },
  { key: "INTERVIEW_INVITE", label: "Interview invite", subject: "Your interview for {{jobTitle}}", body: "Hi {{candidateName}},\n\nYou have been invited to interview for the {{jobTitle}} role. Please click the link below when you are ready to record your answers.\n\n{{interviewUrl}}\n\nBest,\nThe Hiring Team" },
  { key: "REMINDER", label: "Interview reminder", subject: "Reminder: Your interview is pending", body: "Hi {{candidateName}},\n\nJust a quick reminder that your interview for {{jobTitle}} is still pending. The link below expires soon.\n\n{{interviewUrl}}" },
  { key: "SCORE_READY", label: "Score ready", subject: "Your interview score is ready", body: "Hi {{candidateName}},\n\nYour score for {{jobTitle}} has been computed. Log in to see the full report.\n\n{{portalUrl}}" },
];

const ComposeEmail = () => {
  const navigate = useNavigate();
  const templates = FALLBACK_TEMPLATES;
  const [templateKey, setTemplateKey] = useState("COMPOSE");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Compose email - Ample Hire";
    // Previously fetched /common/email-templates to augment FALLBACK_TEMPLATES,
    // but that endpoint never existed and the catch silently fell back. Until
    // there's a real ad-hoc template table, the fallback list is the source.
  }, []);

  const onTemplate = (k) => {
    setTemplateKey(k);
    const t = templates.find((x) => x.key === k);
    if (t && k !== "COMPOSE") {
      setSubject(t.subject || "");
      setBody(t.body || "");
    }
  };

  const send = async () => {
    setError(null); setResult(null);
    if (!to.trim() || !subject.trim()) {
      setError("Recipient and subject are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await axiosInstance.post("/common/email/send", {
        to: to.trim(), subject: subject.trim(), body, templateKey,
      });
      if (data?.success) {
        setResult(data);
      } else {
        setError(data?.message || "Send failed");
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally { setSending(false); }
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Compose email</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            Send a one-off email. Template variables like <code>{"{{candidateName}}"}</code> are substituted at send time.
          </p>
        </div>
        <button onClick={() => navigate("/admin/email-inbox")} style={btnGhost}>← Back to inbox</button>
      </div>

      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: 20 }}>
        <Field label="Template">
          <select value={templateKey} onChange={(e) => onTemplate(e.target.value)} style={input}>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="To *">
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" style={input} />
        </Field>
        <Field label="Subject *">
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Your interview is ready" style={input} />
        </Field>
        <Field label="Body">
          <textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message..." style={{ ...input, fontFamily: "inherit", resize: "vertical" }} />
        </Field>

        {result && (
          <div style={{ marginTop: 12, padding: 12, background: "#ECFDF5", border: "1px solid #10B981", borderRadius: 6, fontSize: 12, color: "#047857" }}>
            ✓ {result.message} <span style={{ fontSize: 11, opacity: 0.7 }}>(log row #{result.id})</span>
            <div style={{ fontSize: 11, color: "#047857", opacity: 0.7, marginTop: 4 }}>
              Stub mode: no actual SMTP/SES call was made. The email is logged in ev_email_log_t.
            </div>
          </div>
        )}
        {error && (
          <div style={{ marginTop: 12, padding: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 6, fontSize: 12, color: "#991B1B" }}>
            ✗ {error}
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => navigate(-1)} style={btnGhost}>Cancel</button>
          <button onClick={send} disabled={sending || !to.trim() || !subject.trim()} style={{ ...btnPrimary, opacity: sending || !to.trim() || !subject.trim() ? 0.5 : 1 }}>
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4, fontWeight: 500 }}>{label}</div>
    {children}
  </div>
);

const input = { width: "100%", padding: "8px 12px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 13, color: "#0F172A", outline: "none", background: "white", boxSizing: "border-box" };
const btnPrimary = { padding: "10px 20px", borderRadius: 6, background: "#7B5AC4", color: "white", border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer" };
const btnGhost = { padding: "8px 16px", borderRadius: 6, background: "white", color: "#475569", border: "1px solid #E2E8F0", fontWeight: 600, fontSize: 12, cursor: "pointer" };

export default ComposeEmail;
